import { Navigate, Outlet, useLocation } from 'react-router';
import { t } from '../i18n';
import { useAuth } from './AuthContext';

/** Route guard: sign-in required. Preserves the target location so login can return to it. */
export function RequireAuth() {
  const { status, signedOutOnPurpose } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <p role="status" className="p-6 text-slate-600">
        {t('auth.loading')}
      </p>
    );
  }
  if (status === 'anonymous') {
    // After signing out the next person starts at the dashboard, not on the page the last one was reading.
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: signedOutOnPurpose ? '/' : location.pathname }}
      />
    );
  }
  return <Outlet />;
}

/** Route guard: signed-in AND holding a permission; otherwise shows an access-denied page. */
export function RequirePermission({
  permission,
  children,
}: {
  permission: string;
  children: React.ReactNode;
}) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <AccessDenied />;
}

/** Like RequirePermission, but a school-scoped grant is also enough (the API narrows the data). */
export function RequireAnyScope({
  permission,
  children,
}: {
  permission: string | string[];
  children: React.ReactNode;
}) {
  const { canAny } = useAuth();
  const codes = Array.isArray(permission) ? permission : [permission];
  return codes.some((code) => canAny(code)) ? <>{children}</> : <AccessDenied />;
}

function AccessDenied() {
  return (
    <div className="mx-auto max-w-xl py-10 text-center">
      <h1 className="text-2xl font-bold">{t('forbidden.title')}</h1>
      <p className="mt-2 text-slate-600">{t('forbidden.body')}</p>
    </div>
  );
}
